"use client";

import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ProviderScheduleTable, ProviderSchedule } from "@/src/components/tasks/provider-schedule-table";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { Referral } from "@/src/models/documents/referral";
import { getFullAddress } from "@/src/models/patient";

// Define the location type
export interface LocationWithDistance {
  name: string;
  address: string;
  coordinates: { latitude: number; longitude: number };
  distance: number;
  scheduleName: string;
}

interface USHVReferralInstructionsProps {
  referral: Referral;
  sortedLocations: LocationWithDistance[];
  schedules: ProviderSchedule[];
  receivingProvider: string | null;
  receivingLocation: string | null;
  receivingProviderOptions: string[];
  receivingLocationOptions: string[];
  nameToScheduleNameMap: Map<string, string>;
}

function customReferralString(referral: Referral): string {
  const missingFields = [];

  if (!referral.diagnosisList?.length) {
    missingFields.push("diagnosisText");
  }

  if (!referral.referringProvider?.familyName) {
    missingFields.push("referringProvider");
  }

  if (missingFields.length > 0) {
    return `referral missing:\n${missingFields.join("\n")}`;
  }

  const diagnosisText = referral.diagnosisList
    ?.map((d: { diagnosisText: string | null }) => d.diagnosisText)
    .filter(Boolean)
    .join("; ");

  return `referral from ${referral.referringProvider!.familyName} dx: ${diagnosisText}`;
}

export function USHVReferralInstructions({
  referral,
  sortedLocations,
  receivingProvider,
  receivingLocation,
  schedules,
  receivingProviderOptions,
  receivingLocationOptions,
  nameToScheduleNameMap,
}: USHVReferralInstructionsProps) {
  const receivingProviderName = receivingProvider ? receivingProvider : "ANY";
  const receivingLocationName = receivingLocation ? receivingLocation : "ANY";
  const referralMessage = customReferralString(referral);
  const patientAddress = getFullAddress(referral.patient);

  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <div className="flex flex-col gap-2">
            <p>
              <span className="font-medium">Receiving Provider:</span>{" "}
              <span className="text-muted-foreground">
                <CopyableContent text={receivingProviderName} />
              </span>
            </p>

            {sortedLocations && sortedLocations.length > 0 ? (
              <>
                <p className="font-medium">Closest Three Locations to Patient:</p>
                <div className="space-y-1">
                  {sortedLocations.map((location, index) => (
                    <div key={location.name}>
                      <p className="font-medium">
                        {index + 1}. {location.name} -{" "}
                        <span className="font-normal text-muted-foreground">{location.address}</span>{" "}
                        <span className="text-xs">(~{location.distance.toFixed(1)} miles)</span>
                      </p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">No nearby locations found</p>
            )}
            <p>
              <span className="font-medium">Referral Message:</span>{" "}
              <span className="text-muted-foreground">
                <CopyableContent text={referralMessage} />
              </span>
            </p>
            <p>
              <span className="font-medium">Patient Address:</span>{" "}
              <span className="text-muted-foreground">
                <CopyableContent text={patientAddress || "N/A"} />
              </span>
            </p>
            <div className="mt-4">
              <ProviderScheduleTable
                schedules={schedules}
                receivingLocationName={receivingLocationName}
                receivingProviderName={receivingProviderName}
                receivingProviderOptions={receivingProviderOptions}
                receivingLocationOptions={receivingLocationOptions}
                nameToScheduleNameMap={nameToScheduleNameMap}
              />
            </div>
          </div>
        </div>
      </EhrInstructionsCard>

      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage!} type="Primary" isEditable />}
      {referral.secondaryCoverage && (
        <CoverageCard coverage={referral.secondaryCoverage!} type="Secondary" isEditable />
      )}
    </div>
  );
}
